import type { CSSProperties, ReactNode } from 'react';
import { motion } from 'motion/react';
import { Sidebar, SidebarProvider } from '@monksflow/monks-ui';
import { sidebarPanelSpring } from '@/shared/constants/motion.constants';

import { FeatureSidebarHeader } from './FeatureSidebarHeader';
import { FeatureSidebarNav } from './FeatureSidebarNav';
import { FeatureSidebarNavItem } from './FeatureSidebarNavItem';
import { useTranslation } from 'react-i18next';

/**
 * One width for every expanded sidebar in the app. Per-surface overrides drifted to
 * 240/250/253 before the design pinned it, which is why this is not a prop.
 */
const SIDEBAR_WIDTH_PX = 280;

interface FeatureSidebarProps {
  children: ReactNode;
  /** Accessible name for the rail. Defaults to a generic one. */
  label?: string;
  /**
   * Keeps the pre-redesign look on the redesigned arm — white `card` surface and the old
   * padding, minus the dividing border. For a panel being replaced rather than restyled,
   * where restyling it would land a change nobody sees through. Goes when that panel goes.
   */
  keepLegacySurface?: boolean;
}

/**
 * The panel's frame: the current design, or the pre-redesign look for a panel
 * awaiting replacement.
 */
function panelChrome({ keepLegacySurface }: Pick<FeatureSidebarProps, 'keepLegacySurface'>) {
  if (keepLegacySurface) {
    return 'bg-card p-4';
  }

  return 'border-r border-border-light px-xs py-sm';
}

/**
 * Feature-level sidebar shell built on the monks-ui Sidebar
 * primitive (mirrors the main SidebarRail). Wrapped in motion.div
 * for a horizontal slide-in on mount.
 *
 * Sub-components: `.Header` / `.Nav` / `.NavItem`.
 */
export function FeatureSidebar(props: FeatureSidebarProps) {
  const { children, label, keepLegacySurface } = props;
  const { t } = useTranslation('common');

  const providerStyle: CSSProperties = {
    '--sidebar-width': `${String(SIDEBAR_WIDTH_PX)}px`,
    width: 'auto',
    minHeight: 0,
    height: '100%',
  } as CSSProperties;

  return (
    <motion.div
      data-testid="feature-sidebar-motion"
      initial={{ x: -SIDEBAR_WIDTH_PX, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      transition={sidebarPanelSpring}
      className="h-full"
    >
      <SidebarProvider defaultOpen style={providerStyle} data-testid="feature-sidebar-wrapper">
        <Sidebar
          collapsible="none"
          // The primitive is a plain div, which an `aria-label` alone does not name —
          // the rail was unreachable by landmark and unnamed when reached.
          role="navigation"
          aria-label={label ?? t('ui.featureSidebar.featureNavigation')}
          className={panelChrome({ keepLegacySurface })}
        >
          {children}
        </Sidebar>
      </SidebarProvider>
    </motion.div>
  );
}

FeatureSidebar.Header = FeatureSidebarHeader;
FeatureSidebar.Nav = FeatureSidebarNav;
FeatureSidebar.NavItem = FeatureSidebarNavItem;
